import { ChangeDetectionStrategy, Component, input, output, signal, inject, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IAddActionsTable, IMiSimpleTableColumn, MiTableSimpleComponent } from 'src/app/shared/components/mi-table-simple/mi-table-simple.component';
import { PersonVehicleService } from '../../services/person-vehicle.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subscription, catchError, finalize, map, of, shareReplay } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ModalInfoVehicularComponent } from '../modal-info-vehicular/modal-info-vehicular.component';
import { ConfirmDeleteDialogComponent } from 'src/app/shared/components/confirm-delete/confirm-delete.component';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { IEmpleadoDocument } from 'src/app/apps/empresas/modules/vinculacion/empleados/interfaces/IEmpleadoDocument';

@Component({
    selector: 'app-info-vehicular',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, StackDirectives, MiTableSimpleComponent],
    templateUrl: './info-vehicular.html',
    styleUrl: './info-vehicular.css'
})
export class InfoVehicular {
    dialog = inject(MatDialog);
    // Inputs/Outputs modern signal style
    @Input() idEncrypt: any;
    @Output() validChange = new EventEmitter<boolean>();

    isMobile = signal<boolean>(false); // Para control responsive, igual que info-academica
    dataSource = signal<any[]>([]);
    columns = signal<IMiSimpleTableColumn[]>([
        { field: 'detalle', header: 'Vehículo', sortable: false, bold: true, align: 'left', type: 'html' },
        { field: 'tipoVehiculoDescripcion', header: 'Tipo de vehículo', sortable: false, align: 'left' },
        { field: 'propietarioTag', header: '¿Es propietario?', sortable: false, align: 'left', type: 'html' }
    ]);
    displayedColumns = signal<string[]>(['detalle', 'tipoVehiculoDescripcion', 'propietarioTag']);
    filterColumns = signal<string[]>(['detalle', 'tipoVehiculoDescripcion', 'propietarioTag']);

    // Servicio de Vehículos (inyectar al constructor o usar inject)
    private personVehicleService = inject(PersonVehicleService);
    private loader = inject(LoaderService);
    private toastr = inject(ToastrService);
    private bp = inject(BreakpointObserver);
    private sub = new Subscription();
    indSinVehiculos = signal<boolean>(false); // Indicador de que el candidato no posee vehículos, para mostrar botón alternativo

    ngOnInit() {
        if (this.idEncrypt) {
            this.cargarVehiculos(this.idEncrypt);
            const s = this.bp
                .observe(['(max-width: 768px)'])
                .pipe(
                    map((r) => r.matches),
                    shareReplay({ bufferSize: 1, refCount: true })
                )
                .subscribe((isSmall) => this.isMobile.set(isSmall));
            this.sub.add(s);
        }
    }

    readonly addActions = signal<IAddActionsTable[]>([
        {
            tooltip: 'Editar',
            icon: 'solar:pen-2-broken',
            action: (row: any) => this.onEdit(row),
            color: '#5b53f3',
            showIf: (row) => row.idTipoVehiculo !== null && row.idTipoVehiculo !== 0
        },
        {
            tooltip: 'Eliminar',
            icon: 'solar:trash-bin-minimalistic-broken',
            action: (row: any) => this.onDelete(row),
            color: 'red',
        },

    ]);

    cargarVehiculos(id: string) {
        this.personVehicleService
            .getAllByCandidate(id)
            .pipe(
                catchError((error) => {
                    this.toastr.error(error?.error?.mensaje || 'No fue posible completar la operación.', 'Error');
                    return of(null);
                }),
                finalize(() => {
                    this.loader.hide();
                    this.updateValidity();
                })
            )
            .subscribe((response) => {
                this.loader.hide();
                if (response) {
                    this.validateNoVehicles(response.data);
                    const vehiculos = (response.data || []).map((item) => {
                        let detalle =
                            item.idTipoVehiculo == null || item.idTipoVehiculo == 0
                                ? 'Sin vehículo'
                                : `<div><b>${item.marca}-${item.referencia}-${item.modelo}</b></div>
            <div><b>Color:</b> ${item.color}</div> 
            <div><b>Placa:</b> ${item.placa}</div>
            <div><b>Transito de:</b> ${item.desCiudad}</div>`;
                        if (item.esPrincipal)
                            detalle +=
                                '<small class="text-sm bg-light-primary text-primary p-1 p-y-4 rounded-sm">Vehículo principal</small>';

                        // Tag visual Sí/No
                        const propietarioTag =
                            item.idTipoVehiculo == null || item.idTipoVehiculo == 0
                                ? ''
                                : item.esPropio
                                  ? '<span class="mi-tag-propietario mi-tag-si">Sí</span>'
                                  : '<span class="mi-tag-propietario mi-tag-no">No</span>';
                        // Validación estricta
                        if (item.idTipoVehiculo !== null && item.idTipoVehiculo !== 0) {
                            if ((item.esPropio && !propietarioTag.includes('Sí')) || (!item.esPropio && !propietarioTag.includes('No'))) {
                                throw new Error('El tag propietario no es correcto');
                            }
                        }
                        return {
                            ...item,
                            detalle: detalle,
                            propietarioTag: propietarioTag
                        };
                    });
                    this.dataSource.set(vehiculos);
                }
            });
    }

    onEdit(row: any) {
        // Abre el modal de editar vehículo
        this.dialog
            .open(ModalInfoVehicularComponent, {
                width: '50%',
                maxWidth: '100vw',
                data: row
            })
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    // Guardar cambios (update)
                    this.loader.show('Actualizando vehículo...');
                    this.personVehicleService
                        .update(this.idEncrypt, response)
                        .pipe(
                            catchError((error) => {
                                this.loader.hide();
                                this.toastr.error(error?.error?.mensaje || 'No fue posible completar la operación.', 'Error');
                                return of(null);
                            }),
                            finalize(() => this.loader.hide())
                        )
                        .subscribe((response) => {
                            if (response) {
                                this.loader.hide();
                                this.toastr.success(response.message || 'Vehículo actualizado exitosamente', 'Éxito');
                                this.loader.show('Cargando vehículos...');
                                this.cargarVehiculos(this.idEncrypt);
                            }
                        });
                }
            });
    }
    openModalVehiculo() {
        // Diálogo agregar vehículo
        this.dialog
            .open(ModalInfoVehicularComponent, {
                width: '50%',
                maxWidth: '100vw',
                data: {
                    idEncrypt: this.idEncrypt,
                    vehiculos: this.dataSource()
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    this.loader.show('Agregando vehículo...');
                    this.personVehicleService
                        .add(this.idEncrypt, result)
                        .pipe(
                            catchError((error) => {
                                this.loader.hide();
                                this.toastr.error(error?.error?.mensaje || 'No fue posible completar la operación.', 'Error');
                                return of(null);
                            }),
                            finalize(() => this.loader.hide())
                        )
                        .subscribe((response) => {
                            if (response) {
                                this.loader.hide();
                                this.toastr.success(response.message || 'Vehículo agregado exitosamente', 'Éxito');
                                this.loader.show('Cargando vehículos...');
                                this.cargarVehiculos(this.idEncrypt);
                            }
                        });
                }
            });
    }

    onDelete(row: any) {
        // Diálogo de confirmación igual académico
        this.dialog
            .open(ConfirmDeleteDialogComponent, {
                width: '400px',
                data: { title: 'Confirmar eliminación', message: '¿Está seguro de que desea eliminar este vehículo?' }
            })
            .afterClosed()
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.loader.show('Eliminando vehículo...');
                    this.personVehicleService
                        .delete(this.idEncrypt, row.id)
                        .pipe(
                            catchError((error) => {
                                this.loader.hide();
                                this.toastr.error(error?.error?.mensaje || 'No fue posible completar la operación.', 'Error');
                                return of(null);
                            }),
                            finalize(() => this.loader.hide())
                        )
                        .subscribe((response) => {
                            if (response) {
                                this.loader.hide();
                                this.toastr.success(response.message || 'Vehículo eliminado correctamente', 'Éxito');
                                this.cargarVehiculos(this.idEncrypt);
                            }
                        });
                }
            });
    }
    // Valida sección completa: al menos un vehículo Y uno principal, o la fila "Sin vehículo"
    // que crea "No poseo vehículos". Esa fila llega con esPrincipal nulo: sin aceptarla aquí,
    // el candidato sin vehículo nunca podía pasar de esta sección.
    private updateValidity(): void {
        if (this.dataSource() === null) {
            this.validChange.emit(false);
            return;
        }
        const list = this.dataSource();
        const valid =
            Array.isArray(list) &&
            list.length > 0 &&
            (list.some((v) => v.esPrincipal === true) || this.indSinVehiculos());
        this.validChange.emit(valid);
    }

    validateNoVehicles(data: any[]) {
        // If no array or empty array, treat as "no vehicles"
        if (!Array.isArray(data) || data.length === 0) {
            this.indSinVehiculos.set(true);
            return;
        }

        // If there is at least one real vehicle (idTipoVehiculo not null/undefined/0),
        // the candidate does have vehicles. The "no vehicles" indicator should be false.
        const hasRealVehicle = data.some(
            (v) => v.idTipoVehiculo !== null && v.idTipoVehiculo !== undefined && v.idTipoVehiculo !== 0
        );

        this.indSinVehiculos.set(!hasRealVehicle);
    }

    noVehiculo() {
        this.personVehicleService
            .addEmpty(this.idEncrypt)
            .pipe(
                catchError((error) => {
                    this.loader.hide();
                    this.toastr.error(error?.error?.mensaje || 'No fue posible completar la operación.', 'Error');
                    return of(null);
                }),
                finalize(() => this.loader.hide())
            )
            .subscribe((response) => {
                if (response) {
                    this.loader.hide();
                    this.toastr.success(response.message || 'Registro agregado exitosamente', 'Éxito');
                    this.loader.show('Cargando vehículos...');
                    this.cargarVehiculos(this.idEncrypt);
                }
            });
    }
}
